import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { filterBy } from "@progress/kendo-data-query";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { EditorUtils } from "@progress/kendo-react-editor";
import { Input } from "@progress/kendo-react-inputs";
import { Label } from "@progress/kendo-react-labels";
import { Popup } from "@progress/kendo-react-popup";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { ySyncPluginKey } from "y-prosemirror";
import * as Y from "yjs";
import { icon, label, notifyIcon, number, placeholder, tooltip } from "../../../../config";
import { getNotification } from "../../../../utils/common";
import { formatUserTimestamp } from "../../sidebar.common";
import { addActivityLog } from "../ActivityLog/activityLog.service";
import { getVersionHistory, getPreviewVersion, createManualVersion, restoreManualVersion } from "../../../../shared/services/editor.service";
import { DOCUMENT_CONTEXT_TYPE } from "../../../ContentManagementSystem/ContentManager/contentManager.constants";
import { DESCRIPTION_RESTORE_VERIFY_ELEMENT_ID, getVersionName, VERSION_TYPES } from "../../../../editor/editor.Constant";

const CURRENT_VERSION_OBJ = { label: label.CURRENT_VERSION, isCurrent: true, group: "Current Version" };

/**
 * Returns the ProseMirror `EditorView` for Kendo React `<Editor ref={editorRef} />`.
 * `EditorUtils.setHtml` requires this view; use `state.view` when set during mount, otherwise the `view` getter.
 * @param {React.RefObject} editorRef - Ref attached to Kendo `Editor`
 * @returns {import("prosemirror-view").EditorView | undefined}
 * @author Abhishek Dhillon
 */
const getKendoEditorView = (editorRef) => {
  const instance = editorRef?.current;
  if (!instance) return undefined;
  return instance.state?.view ?? instance.view;
};

/**
 * Applies HTML to the Kendo editor using `EditorUtils.setHtml` (Kendo’s API for the ProseMirror-backed editor).
 * @param {React.RefObject} editorRef - Ref attached to Kendo `Editor`
 * @param {string} html - HTML string
 * @author Abhishek Dhillon
 */
const setKendoEditorHtml = (editorRef, html) => {
  const view = getKendoEditorView(editorRef);
  if (!view) return;
  EditorUtils.setHtml(view, html, undefined, { preserveWhitespace: "full" });
};

const injectRestoreVerifyIdOnFirstParagraph = (html, id) => {
  const doc = new DOMParser().parseFromString(html, "text/html");
  const paragraphs = doc.querySelectorAll("p");
  if (paragraphs.length > 0) {
    paragraphs[0].setAttribute("id", id);
  }
  return doc.body.innerHTML;
};

const DescriptionHistory = ({ 
  restoredHtml,
  setRestoredHtml,
  restoredHtmlApplyNonce,
  setRestoredHtmlApplyNonce,
  provider,
  editorState,
  yjsDocRef, 
  viewRef, 
  desc,
  setDesc, 
  editorRef, 
  collab, 
  setVersionPreviewMode, 
  versionPreviewMode, 
  versionViewRef,
  type = ""
}) => {
  const anchorTag = useRef();
  const [version, setVersion] = useState(CURRENT_VERSION_OBJ);
  const [versionList, setVersionList] = useState([]);
  const { user } = useSelector((state) => state.auth);
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [snapshotMode, setSnapshotMode] = useState(false);
  const { task } = useSelector((state) => state.taskSidebar);

  useEffect(() => {
    if(
      restoredHtml && 
      restoredHtmlApplyNonce.value > 0 && 
      restoredHtmlApplyNonce.value < number.FOUR && 
      restoredHtmlApplyNonce.idToIdentify && 
      editorState === "connected"
    ) {
      if(checkHtmlMatch()) {
        setRestoredHtml(null);
        setRestoredHtmlApplyNonce(prev => ({ ...prev, value: number.ZERO, idToIdentify: null }));
      } else {
        setKendoEditorHtml(editorRef, restoredHtml);
        setRestoredHtmlApplyNonce(prev => ({ ...prev, value: prev.value + number.ONE }));
      }
    }
  }, [restoredHtml, restoredHtmlApplyNonce]);

  const checkHtmlMatch = () => {
    const view = editorRef.current.view;
    const viewHTML = EditorUtils.getHtml(view.state);
    const parser = new DOMParser();
    const doc = parser.parseFromString(viewHTML, "text/html");
    const paragraphId = doc.querySelector(`[id="${restoredHtmlApplyNonce.idToIdentify}"]`);
    if(paragraphId?.id === restoredHtmlApplyNonce.idToIdentify) {
      return true;
    }
    return false;
  }

  /**
   * Add Current Y-Doc version as a snapshot
   */
  const addVersion = async (type) => {
    const ydoc = yjsDocRef.current;
    const versions = ydoc.getArray("versions");
    const prevVersion = versions.length === 0 ? null : versions.get(versions.length - 1);
    const prevSnapshot = prevVersion === null ? Y.emptySnapshot : Y.decodeSnapshot(prevVersion.snapshot);
    const snapshot = Y.snapshot(ydoc);
    if (prevVersion != null) {
      // account for the action of adding a version to ydoc
      const prevVersionID = prevSnapshot.sv.get(prevVersion.clientID);
      prevSnapshot.sv.set(prevVersion.clientID, prevVersionID + 1);
    }
    if (!Y.equalSnapshots(prevSnapshot, snapshot)) {
      const versionDate = new Date();
      versions.push([
        {
          date: versionDate.getTime(),
          snapshot: Y.encodeSnapshot(snapshot),
          clientID: ydoc.clientID,
          user: user.name,
          label: null,
        },
      ]);
      setVersionList([...versions.toArray()].reverse());
      getNotification(label.ADD_VERSION_MSG, notifyIcon.SUCCESS_ICON);
      if (type !== "restore") {
        const payload = {
          type: "add version",
          userId: user.id,
          taskId: task?.taskId,
        };
        if (type !== DOCUMENT_CONTEXT_TYPE.CMS) {
          await addActivityLog(payload);
        }
      }
    }
  };

  /**
   * Render Snapshot as a view to the editor
   * @param {Y.Snapshot} version
   */
  const renderVersion = (version) => {
    const editorView = viewRef.current;
    try {
      const encodedSnapshot = version?.snapshot;
      const snapshot = Y.decodeSnapshot(encodedSnapshot);
      if (snapshot) {
        // Update editor state with the selected snapshot
        const binding = ySyncPluginKey.getState(editorView.state).binding;
        if (binding) {
          binding.renderSnapshot(snapshot, Y.emptySnapshot);
        }
      }
    } catch (error) {
      getNotification(error?.message || error, notifyIcon.ERROR_ICON);
    }
  };

  /**
   * Reflect to the current editable version.
   */
  const unrenderVersion = () => {
    if(versionPreviewMode?.show) {
      setVersionPreviewMode(prev => ({ ...prev, show: false, versionData: null, key: number.ZERO, loading: false }));
    } else {
      const editorView = viewRef?.current;
      const binding = ySyncPluginKey.getState(editorView.state).binding;
      if (binding != null) {
        binding.unrenderSnapshot();
      }
    }
    setVersion(CURRENT_VERSION_OBJ);
    setSnapshotMode(false);
  };

  /**
   * Handle version list change event
   * @param {Event} e
   */
  const handleListChange = async (e) => {
    try {
      const selectedItem = e.value; // Access the selected item
      if (selectedItem.isCurrent) {
        // Logic for "Current Version"
        unrenderVersion();
        setVersionPreviewMode(prev => ({ ...prev, show: false, versionData: null, key: number.ZERO, loading: false }));
      } else {
        // Logic for other versions
        const selectedIndex = e.target.index;
        if (selectedIndex >= 0) {
          setSnapshotMode(true);
          const selectedVersion = versionList[selectedIndex - number.ONE]; // Adjust index to skip the placeholder
          if(selectedVersion?.snapShotType === "manual" || selectedVersion?.metadata) {
            setVersionPreviewMode(prev => ({ ...prev, loading: true }));
            const result = await getPreviewVersion(selectedVersion?.id)
            if(result && result?.documentState) {
              setVersionPreviewMode(prev => ({ ...prev, show: true, versionData: result, key: versionPreviewMode?.key + number.ONE, loading: false }));
              setInputValue(prepareVersionObj(result).label);
              setVersion(prepareVersionObj(result));
            } else {
              setVersionPreviewMode(prev => ({ ...prev, loading: false }));
            }
          } else {
            setInputValue(prepareVersionObj(selectedVersion).label);
            setVersion(prepareVersionObj(selectedVersion));
            setVersionPreviewMode(prev => ({ ...prev, show: false, versionData: null, key: number.ZERO, loading: false }));
            renderVersion(prepareVersionObj(selectedVersion));
          }
        }
      }
      setIsEditing(false);
    } catch (error) {
      getNotification(error?.message || error, notifyIcon.ERROR_ICON);
    }
  };

  /**
   * Handle Droplist Label view
   * @param {Object} v
   * @returns
   */
  const prepareVersionObj = (v) => {
    if(v?.versionName || v?.metadata ) {
      return {
        ...v,
        label: v?.versionName,
      }
    }
    const yDoc = yjsDocRef.current;
    return {
      ...v,
      clientID: v?.clientID ?? yDoc.clientID,
      date: new Date(v.date).toLocaleString(),
      user: v?.user ?? user.name,
      label: v?.label ? v.label : formatUserTimestamp(v.user ? v.user : user.name, v?.date),
    };
  };

  /**
   * Fetches the list of snapshots (versions) from the Y.Doc and updates the state.
   */
  const fetchSnapshots = () => {
    const ydoc = yjsDocRef.current;
    const versions = ydoc.getArray("versions");
    setVersionList([...versions.toArray()].reverse());
  };

  const handleVersionList = () => {
    setIsEditing(false);
    setDropdownOpen(true);
  };

  /**
   * Filters the list of snapshots based on the provided filter criteria.
   * @param {Event} e - The event triggered by the filter input (e.g., user typing or selecting a filter).
   */
  const filterSnapshots = (e) => {
    setVersionList(
      filterBy(
        versionList.map((v) => prepareVersionObj(v)),
        e.filter
      )
    );
  };

  /**
   * Restore a particular snapshot to the editor for editing.
   */
  const restoreVersion = async () => {
    try {
      await addVersion("restore");
      const editorContent = viewRef?.current?.dom?.innerHTML;
      unrenderVersion();
      EditorUtils.setHtml(editorRef?.current?.view, stripYChangeTags(editorContent));
      setDesc(editorContent);
      getNotification(label.VERSION_RESTORED_MSG, notifyIcon.SUCCESS_ICON);
      const payload = {
        type: "restore version",
        userId: user.id,
        taskId: task?.taskId,
      };
      if (type !== DOCUMENT_CONTEXT_TYPE.CMS) {
        await addActivityLog(payload);
      }
    } catch (error) {
      getNotification(error?.message || error, notifyIcon.ERROR_ICON);
    }
  };

  const stripYChangeTags = (html) => {
    return html.replace(/<ychange[^>]*>(.*?)<\/ychange>/gi, "$1");
  };

  /**
   * Rename version name
   */
  const updateVersionName = () => {
    const ydoc = yjsDocRef.current;
    const versions = ydoc.getArray("versions");
    const versionsArr = versions.toArray();
    const index = versionsArr.findIndex((v) => new Date(v.date).toLocaleString() === new Date(version.date).toLocaleString());
    if (index > -number.ONE && index < versions.length && inputValue.trim()) {
      const updatedVersion = { ...versions.get(index), label: inputValue.trim() };
      // Update the Y.js array
      versions.delete(index, number.ONE);
      versions.insert(index, [updatedVersion]);
      setVersionList([...versions.toArray()]);
      setIsEditing(false);
      getNotification(label.UPDATE_VERSION_NAME_MSG, notifyIcon.SUCCESS_ICON);
    } else {
      setIsEditing(false);
    }
    unrenderVersion();
  };

  const deleteVersion = async (dataItem) => {
    const ydoc = yjsDocRef.current;
    const versions = ydoc.getArray("versions");
    const versionsArr = versions.toArray();
    const index = versionsArr.findIndex((v) => new Date(v.date).toLocaleString() === new Date(dataItem.date).toLocaleString());
    if (index > -number.ONE && index < versions.length) {
      versions.delete(index, number.ONE);
      setVersionList([...versions.toArray()]);
      getNotification(label.DELETE_VERSION_MSG, notifyIcon.SUCCESS_ICON);
    }
    unrenderVersion();
  };

  // Custom Render Kendo-Dropdown List (Version Label + Edit icon)
  const itemRender = (li, itemProps) => {
    const { dataItem } = itemProps;
    const isManualVersion = dataItem?.snapShotType === "manual" || dataItem?.metadata;

    return React.cloneElement(li, { ...li.props, className: "position-relative px-3 py-2 history-list-icon cursor-pointer" }, [
      <Tooltip anchorElement='target' position='bottom' parentTitle='true'>
        <div key={dataItem.label} className='item-label text-truncate w-100 d-flex justify-content-between'>
          <span>{dataItem.label}</span>
          {!dataItem.isCurrent && !isManualVersion && (
            <div className='d-flex gap-2'>
              <div
                className='text-right edit-history-icon w-100'
                title={tooltip.EDIT_VERSION_NAME}
                onClick={(e) => {
                  e.stopPropagation();
                  setDropdownOpen(false);
                  setVersion(dataItem);
                  setInputValue(prepareVersionObj(dataItem).label);
                  setIsEditing(true);
                }}>
                <span>{icon.EDIT_ICON}</span>
              </div>
              <div
                className='text-right edit-history-icon w-100'
                title={"Delete Version"}
                onClick={(e) => {
                  e.stopPropagation();
                  setDropdownOpen(false);
                  setVersion(dataItem);
                  deleteVersion(dataItem);
                }}>
                <span>{icon.DELETE_ICON}</span>
              </div>
            </div>
          )}
        </div>
      </Tooltip>,
    ]);
  };

  /**
   * Handle Version List Open
   */
  const handleVersionListOpen = async () => {
    handleVersionList();
    setVersionPreviewMode(prev => ({ ...prev, loading: true }));
    const response = await getVersionHistory(collab?.YdocRoom);
    if(response && response?.length > 0) {
      let sortedVersionList = response?.map(v => ({
        ...v,
        snapShotType: "manual",
        group: VERSION_TYPES[v?.versionType]?.group,
        versionName: getVersionName(VERSION_TYPES[v?.versionType]?.label(v?.versionName), v?.timestamp)
      }))?.sort((a,b) => b?.group?.localeCompare(a?.group));
      setVersionList(sortedVersionList ?? []);
    }
    setVersionPreviewMode(prev => ({ ...prev, loading: false }));
  };

  /**
   * Handle Add Version
   */
  const addVersionHandler = async () => {
    // addVersion();
    setVersionPreviewMode(prev => ({ ...prev, loading: true }));
    const response = await createManualVersion({
      docId: collab?.YdocRoom,
      username: user.name,
      ...(type === DOCUMENT_CONTEXT_TYPE.CMS ? { type: DOCUMENT_CONTEXT_TYPE.CMS } : {}),
    });
    if(response) {
      getNotification(label.ADD_VERSION_MSG, notifyIcon.SUCCESS_ICON);
      const payload = {
        type: "add version",
        userId: user.id,
        taskId: task?.taskId,
      };
      if (type !== DOCUMENT_CONTEXT_TYPE.CMS) {
        await addActivityLog(payload);
      }
    }
    setVersionPreviewMode(prev => ({ ...prev, loading: false }));
  };

  const handleOnClose = () => {
    setDropdownOpen(false);
  }

  /**
   * Handle Restore Version
   */
  const restoreVersionHandler = async () => {
    try {
      const { show, versionData } = versionPreviewMode;
      if(show && versionData) {
        const versionHtml = versionViewRef?.current?.dom?.innerHTML;
        if(versionHtml) {
          unrenderVersion();
          setVersionPreviewMode(prev => ({ ...prev, show: false, versionData: null, key: number.ZERO, loading: true }));
          const result = await createManualVersion({
            docId: collab?.YdocRoom,
            username: user.name,
            ...(type === DOCUMENT_CONTEXT_TYPE.CMS ? { type: DOCUMENT_CONTEXT_TYPE.CMS } : {}),
          });
          if(result) {
            const stripped = stripYChangeTags(versionHtml);
            let pId = `restore-${result}`;
            let idHtml = injectRestoreVerifyIdOnFirstParagraph(stripped, pId);
            setDesc(idHtml);
            EditorUtils.setHtml(editorRef?.current?.view, idHtml);
            setRestoredHtml(idHtml);
            setRestoredHtmlApplyNonce(prev => ({ ...prev, value: number.ONE, idToIdentify: pId }));
            setVersionPreviewMode(prev => ({ ...prev, loading: false }));
          } else {
            setVersionPreviewMode(prev => ({ ...prev, loading: false }));
          }
        }
      } else {
        restoreVersion();
      }
    }
    catch(error) {
      getNotification(error?.message || error, notifyIcon.ERROR_ICON);
    }
    finally {
      setVersionPreviewMode(prev => ({ ...prev, loading: false }));
    }
  }

  const groupHeaderItemRender = (li) => {
    const item = (
      <span className="font-size-fourteen">
        {li.props.children}
      </span>
    );
    return React.cloneElement(li, li.props, item);
  };

  const stickyHeaderRender = (div) => {
    const item = (
        <span className="font-size-fourteen">
            {div.props.children}
        </span>
    );
    return React.cloneElement(div, div.props, item);
  };



  return (
    <div className='d-flex align-items-center' ref={anchorTag}>
      <Label className='mb-0 mr-1'>Version:</Label>
      <DropDownList
        id='snapshot-history'
        data={[
          CURRENT_VERSION_OBJ, // Add "Current Version" at the top
          ...versionList.map((v) => prepareVersionObj(v)),
        ]}
        key={"id"}
        value={version}
        textField='label'
        onChange={(e) => handleListChange(e)}
        itemRender={itemRender} // Use custom render
        filterable={true}
        onFilterChange={(e) => filterSnapshots(e)}
        onOpen={handleVersionListOpen}
        onClose={handleOnClose}
        opened={dropdownOpen}
        size={"small"}
        valueRender={(element, value) => {
          return React.cloneElement(element, { ...element.props }, <span>{value?.label}</span>);
        }}
        title={version?.label ?? ""}
        loading={versionPreviewMode?.loading}
        className="version-dropdown"
        popupSettings={{ className: "dropdown-popup-settings", appendTo: anchorTag.current }}
        leftRightKeysNavigation={true}
        adaptive={true}
        groupField={"group"}
        groupStickyHeaderItemRender={stickyHeaderRender}
        groupHeaderItemRender={groupHeaderItemRender}
      />
      {isEditing && anchorTag && (
        <Popup anchor={anchorTag.current} show={isEditing} position='bottom' collision={{ vertical: "fit", horizontal: "fit" }}>
          <div className='popup-content'>
            <h6>{tooltip.EDIT_VERSION_NAME}</h6>
            <div className='d-flex align-items-center'>
              <Input
                maxLength={number.EIGHTY} // max characters
                value={inputValue} // input value
                onChange={(e) => setInputValue(e.target.value)} // onChange Handler
                placeholder={placeholder.WRITE_SOMETHING} // placeholder
                required={true}
              />
              <span
                className={`ml-3 mr-2 text-green ${inputValue.trim().length === 0 ? "disabled" : ""}`}
                onClick={(e) => {
                  e.preventDefault();
                  if (inputValue.trim().length > 0) {
                    updateVersionName();
                  }
                }}>
                {icon.DONE}
              </span>
              <span
                onClick={() => {
                  setIsEditing(false);
                  unrenderVersion();
                }}>
                {icon.CLEAR}
              </span>
            </div>
          </div>
        </Popup>
      )}
      <Tooltip anchorElement='target' position='bottom' parentTitle='true'>
        <div className='d-flex'>
          {!snapshotMode && (
            <div 
              className='font-weight-semi-bold ml-3  cursor-pointer' 
              title={label.SAVE_VERSION} 
              onClick={addVersionHandler}
              disabled={versionPreviewMode?.loading}>
              <div className='d-flex align-items-center'>
                <span className='app-icon-medium app-icon-box'>{icon.SAVE_SNAPSHOT}</span>
              </div>
            </div>
          )}
          {snapshotMode && (
            <div
              className={`ml-3 font-weight-semi-bold ${!version?.isCurrent ? "cursor-pointer" : "disabled pointer-events-none"}`}
              title={label.RESTORE}
              onClick={restoreVersionHandler}>
              <div className='d-flex align-items-center'>
                <span className='app-icon-medium app-icon-box'>{icon.RESTORE_SNAPSHOT}</span>
              </div>
            </div>
          )}
        </div>
      </Tooltip>
    </div>
  );
};

export default DescriptionHistory;
